import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Loader2,
  Shield,
  CheckCircle,
  XCircle,
  ArrowRight,
  Sparkles,
  ClipboardCheck,
  MessageCircle,
  FlaskConical,
  Stethoscope,
  Wallet,
  CalendarCheck,
} from 'lucide-react';
import { markCTAClicked } from '../components/EmailCapturePopup';

const API = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStartMembership = async () => {
    markCTAClicked();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/api/payments/direct-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Unable to process payment. Please try again.');
      setIsLoading(false);
    }
  };

  const included = [
    {
      Icon: Stethoscope,
      title: 'Onboarding consultation',
      desc: 'A real conversation about your symptoms, history, and goals. Not 7 minutes. As long as it takes.',
    },
    {
      Icon: ClipboardCheck,
      title: 'Personalized treatment plan',
      desc: 'A clinical plan built specifically around your labs, symptoms, and life, not a copy paste protocol.',
    },
    {
      Icon: Sparkles,
      title: 'Ongoing prescription management',
      desc: 'Continuous refills and dose adjustments for any medications related to your treatment plan.',
    },
    {
      Icon: MessageCircle,
      title: 'Direct messaging with your care team',
      desc: 'Questions between visits? Send a message. Real responses from real clinicians, not a chatbot.',
    },
    {
      Icon: FlaskConical,
      title: 'Lab review and interpretation',
      desc: 'Your provider reviews every lab result and walks you through what it actually means for your plan.',
    },
    {
      Icon: CalendarCheck,
      title: 'Regular follow-up visits',
      desc: 'Scheduled check-ins to track progress, adjust your plan, and make sure you keep getting better.',
    },
  ];

  const notIncluded = [
    'The cost of any lab work itself',
    'The cost of medications dispensed by a pharmacy',
  ];

  const comparisons = [
    {
      label: 'A primary care visit',
      cost: '$200 - $400 per visit',
      problem: 'Often 7 minutes, a single TSH, no real plan.',
    },
    {
      label: 'A concierge clinic',
      cost: '$3,000 - $8,000 per year',
      problem: 'Excellent access, but rarely women\'s hormone specialists.',
    },
    {
      label: 'A wellness "package"',
      cost: '$2,000 - $5,000 upfront',
      problem: 'High-pressure sales, expensive supplements, limited follow-up.',
    },
    {
      label: 'Glow Health Membership',
      cost: '$99 / month',
      problem: 'Specialist care, real time, ongoing adjustments. Cancel anytime.',
      isUs: true,
    },
  ];

  const faqs = [
    {
      q: 'What does the $99 actually cover?',
      a: 'Your onboarding consultation, personalized treatment plan, ongoing prescription management, direct messaging with your care team, lab review and interpretation, and regular follow-up visits. Everything required for a clinician to manage your hormone health long-term.',
    },
    {
      q: 'Are labs included in the membership?',
      a: 'No. Lab work is paid separately. Your provider only orders labs that are clinically appropriate based on your symptoms, and we always discuss costs with you before anything is ordered. We also have referral relationships with labs that offer reasonable cash-pay rates, and we will point you to the most affordable option in your area.',
    },
    {
      q: 'How much do labs typically cost?',
      a: 'It depends entirely on which panels you need, but cash-pay rates through our referral partners are usually a fraction of what insurance copays look like. Most patients pay between $100 and $350 for an initial comprehensive panel, and far less for follow-up rechecks. Your provider will always discuss the exact cost upfront.',
    },
    {
      q: 'Are medications included?',
      a: 'No. Medications are filled at a pharmacy and billed separately, just like any other prescription. We work with affordable pharmacies and can discuss generic and compounded options to keep your costs as low as possible.',
    },
    {
      q: 'Can I use my HSA or FSA?',
      a: 'Yes. Most patients pay their monthly membership using HSA or FSA tax-free dollars. We can also provide superbills for potential out-of-network reimbursement.',
    },
    {
      q: 'Do you accept insurance?',
      a: 'We are currently out-of-network with most insurance plans. This is intentional, it lets us spend real time with you instead of fighting insurance companies for 7-minute visit reimbursements. We provide superbills if you want to file for reimbursement on your own.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes. Memberships are month-to-month with no contract and no cancellation fee. For best results we recommend staying with treatment for at least 3 to 6 months, but you are never locked in.',
    },
    {
      q: 'What if I do not feel better?',
      a: 'If you do not feel meaningfully better within 60 days of starting your plan, we keep working with you for free until you do. No fine print, no expiration. If your plan is not working, that is on us.',
    },
  ];

  return (
    <div className="pricing-v2">
      {/* HERO */}
      <section className="pricing-v2-hero">
        <div className="container">
          <div className="pricing-v2-hero-inner">
            <span className="pricing-v2-eyebrow">Transparent Pricing</span>
            <h1 className="pricing-v2-headline">
              One simple monthly membership.<br />
              <span className="pricing-v2-headline-accent">No surprise bills. No high-pressure upsells.</span>
            </h1>
            <p className="pricing-v2-sub">
              You should know exactly what your care costs before you book. Here is everything, in plain language.
            </p>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CARD */}
      <section className="pricing-v2-plan">
        <div className="container">
          <div className="pricing-v2-plan-grid">
            {/* Left: the card */}
            <div className="pricing-v2-card">
              <div className="pricing-v2-card-badge">One Simple Plan</div>
              <h2 className="pricing-v2-card-title">Glow Health Membership</h2>
              <div className="pricing-v2-price">
                <span className="pricing-v2-price-amount">$99</span>
                <span className="pricing-v2-price-period">/ month</span>
              </div>
              <p className="pricing-v2-card-tagline">
                Cancel anytime. HSA and FSA accepted.
              </p>

              <div className="pricing-v2-section-divider">What is included</div>
              <ul className="pricing-v2-includes">
                {included.map((item, i) => (
                  <li key={i}>
                    <span className="pricing-v2-includes-icon">
                      <item.Icon size={18} strokeWidth={1.7} />
                    </span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="pricing-v2-section-divider muted">What is not included</div>
              <ul className="pricing-v2-excludes">
                {notIncluded.map((item, i) => (
                  <li key={i}>
                    <XCircle size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="pricing-v2-excludes-note">
                Your provider only orders labs that are clinically appropriate, and we will always discuss costs with you before anything is ordered. See the lab section below for typical pricing and our referral partners.
              </p>

              <button
                className="pricing-v2-cta"
                onClick={handleStartMembership}
                disabled={isLoading}
                data-testid="start-membership-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="pricing-v2-spinner" size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    Start Your Membership
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
              {error && (
                <p className="pricing-v2-error" data-testid="membership-error">{error}</p>
              )}
              <p className="pricing-v2-card-foot">
                Billed monthly. No contracts. No cancellation fees.
              </p>
            </div>

            {/* Right: 60-day promise + value props */}
            <div className="pricing-v2-side">
              <div className="pricing-v2-promise">
                <div className="pricing-v2-promise-icon">
                  <Shield size={24} />
                </div>
                <h3>The 60-day promise</h3>
                <p>
                  If you do not feel meaningfully better within 60 days of starting your plan, we keep working with you for free until you do. No fine print. No expiration.
                </p>
              </div>

              <div className="pricing-v2-stack">
                <div className="pricing-v2-stack-item">
                  <Wallet size={18} />
                  <span>HSA and FSA accepted, most patients pay tax-free.</span>
                </div>
                <div className="pricing-v2-stack-item">
                  <Sparkles size={18} />
                  <span>Cancel any time, no contracts, no penalties.</span>
                </div>
                <div className="pricing-v2-stack-item">
                  <FlaskConical size={18} />
                  <span>Affordable lab referrals, your provider helps you find the best rate.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW LABS WORK */}
      <section className="pricing-v2-labs">
        <div className="container">
          <div className="pricing-v2-labs-inner">
            <div className="pricing-v2-labs-content">
              <span className="pricing-v2-eyebrow">How labs work</span>
              <h2 className="pricing-v2-section-headline">
                Honest, upfront, and on your side.
              </h2>
              <p className="pricing-v2-labs-body">
                Lab work is one of the most overpriced and confusing parts of healthcare, and we refuse to play that game. Here is exactly how labs work at Glow Health:
              </p>
              <ol className="pricing-v2-labs-steps">
                <li>
                  <strong>Your provider only orders labs that are clinically appropriate</strong>, never as an upsell, never as a default panel for every patient.
                </li>
                <li>
                  <strong>You see the cost before anything is ordered.</strong> No mystery bills weeks later.
                </li>
                <li>
                  <strong>We refer you to lab partners with reasonable cash-pay rates</strong>, often a fraction of what insurance copays look like for the same tests.
                </li>
                <li>
                  <strong>You can also use insurance or any lab you prefer</strong>, we will work with whatever fits your situation best.
                </li>
              </ol>
              <p className="pricing-v2-labs-foot">
                <strong>Typical investment:</strong> most patients pay between $100 and $350 for an initial comprehensive panel through our referral partners, and considerably less for follow-up rechecks. Your provider will always confirm the exact cost in advance.
              </p>
            </div>
            <div className="pricing-v2-labs-visual">
              <div className="pricing-v2-labs-card">
                <FlaskConical size={28} />
                <h4>Why we do it this way</h4>
                <p>
                  Bundling labs into a flat monthly fee sounds simple, but it almost always means patients overpay or under-test. Keeping labs separate keeps your monthly cost low, your testing precise, and your wallet protected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="pricing-v2-compare">
        <div className="container">
          <div className="pricing-v2-section-head">
            <span className="pricing-v2-eyebrow">How we compare</span>
            <h2 className="pricing-v2-section-headline">
              The math is not subtle.
            </h2>
            <p className="pricing-v2-section-sub">
              Specialist hormone care is usually expensive, slow, or both. We built a model that breaks that pattern.
            </p>
          </div>
          <div className="pricing-v2-compare-grid">
            {comparisons.map((c, i) => (
              <div
                key={i}
                className={`pricing-v2-compare-card ${c.isUs ? 'is-us' : ''}`}
                data-testid={`compare-${i}`}
              >
                <span className="pricing-v2-compare-label">{c.label}</span>
                <span className="pricing-v2-compare-cost">{c.cost}</span>
                <p className="pricing-v2-compare-problem">{c.problem}</p>
                {c.isUs && <span className="pricing-v2-compare-tag">Us</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-v2-faq">
        <div className="container">
          <div className="pricing-v2-section-head">
            <span className="pricing-v2-eyebrow">Common questions</span>
            <h2 className="pricing-v2-section-headline">What women ask before they join</h2>
          </div>
          <div className="pricing-v2-faq-grid">
            {faqs.map((f, i) => (
              <div key={i} className="pricing-v2-faq-item">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pricing-v2-final">
        <div className="container">
          <div className="pricing-v2-final-inner">
            <h2>Ready to feel like yourself again?</h2>
            <p>
              Start with a free 15-minute consultation. No pressure, no commitment, just a real conversation about whether Glow Health is the right fit for you.
            </p>
            <div className="pricing-v2-final-actions">
              <Link to="/book" className="pricing-v2-btn-light">
                Book Your Free Consultation
                <ArrowRight size={18} />
              </Link>
              <button
                className="pricing-v2-btn-outline"
                onClick={handleStartMembership}
                disabled={isLoading}
                data-testid="cta-start-membership-btn"
              >
                {isLoading ? 'Processing...' : 'Start Your Membership'}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
